/**
 * The composer's call controls (RFC 0307 Design): the button that starts a
 * call, and, while one is on, a speaker toggle and a mic with the person's
 * level. Drawn by the composer from a `ComposerVoice`; nothing here holds a
 * call of its own.
 */

import { AudioLines, Mic, MicOff, Volume2, VolumeX } from 'lucide-react'
import type { ComposerVoice } from '../../services/voice/types'

/** The empty composer's action when nothing is running: start a call. */
export function StartCallButton({
  connecting,
  onClick,
}: {
  connecting: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={connecting}
      className="flex size-8 items-center justify-center rounded-full bg-foreground text-background transition-all active:scale-90"
      aria-label="Talk to the agent"
      title="Talk to the agent"
    >
      {connecting ? (
        <span className="size-3.5 animate-spin rounded-full border-2 border-background/40 border-t-background" />
      ) : (
        <AudioLines className="size-4" strokeWidth={2.5} />
      )}
    </button>
  )
}

/** On a call, the empty composer's action ends it. Never the agent's turn. */
export function EndCallButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-8 items-center justify-center rounded-full bg-foreground/[0.12] text-foreground transition-all hover:bg-foreground/[0.18] active:scale-90"
      aria-label="End call"
      title="End call"
    >
      <span className="size-2.5 rounded-[2px] bg-current" />
    </button>
  )
}

/** How tall each of the six level bars is, from the person's level. */
export function levelBars(level: number): number[] {
  const shape = [0.45, 0.8, 1, 0.65, 0.35, 0.2]
  return shape.map((s) => Math.max(3, Math.round(3 + 13 * level * s)))
}

/** Beside `+` while a call is on: mute the voice, mute yourself. */
export function CallControls({ voice }: { voice: ComposerVoice }) {
  const live = voice.status === 'live'
  return (
    <>
      <button
        type="button"
        onClick={voice.toggleSpeaker}
        disabled={!live}
        aria-label={voice.speakerMuted ? 'Unmute the voice' : 'Mute the voice'}
        title={voice.speakerMuted ? 'Unmute the voice' : 'Mute the voice'}
        className={`flex size-7 items-center justify-center rounded-full transition-colors hover:bg-muted ${
          voice.speakerMuted ? 'text-muted-foreground' : 'text-foreground'
        }`}
      >
        {voice.speakerMuted ? (
          <VolumeX className="size-4" aria-hidden />
        ) : (
          <Volume2 className="size-4" aria-hidden />
        )}
      </button>
      <button
        type="button"
        onClick={voice.toggleMic}
        disabled={!live}
        aria-label={voice.micMuted ? 'Unmute your mic' : 'Mute your mic'}
        title={voice.micMuted ? 'Unmute your mic' : 'Mute your mic'}
        className={`flex h-7 items-center gap-2 rounded-full border border-border px-2.5 transition-colors hover:bg-muted ${
          voice.micMuted ? 'text-muted-foreground' : 'text-foreground'
        }`}
      >
        <span className="flex items-center gap-[2px]" aria-hidden>
          {levelBars(voice.micMuted || !live ? 0 : voice.level).map((h, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: six fixed bars
              key={i}
              className={`w-[2.5px] rounded-full transition-[height] duration-75 ${
                voice.micMuted ? 'bg-muted-foreground/50' : 'bg-blue-500'
              }`}
              style={{ height: h }}
            />
          ))}
        </span>
        {voice.micMuted ? (
          <MicOff className="size-4" aria-hidden />
        ) : (
          <Mic className="size-4" aria-hidden />
        )}
      </button>
    </>
  )
}

/** While the agent works the main button is Stop; this starts a call beside it. */
export function TalkWhileWorkingButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      aria-label="Talk to the agent"
      title="Talk to the agent while it works"
    >
      <AudioLines className="size-4" strokeWidth={2.25} aria-hidden />
    </button>
  )
}
